import useI18n from 'hooks/I18n/useI18n';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import UserRegistration from './UserRegistration.jsx';
import * as R from 'ramda';
import { validate, validators } from 'modules/validation';
import { dateStringToDateObject } from 'models/date';
import { fetchCountriesIfNeeded } from 'modules/countries';
import { register } from 'api/users';

const UserRegistrationContainer = ({ role, assemblyId, onSuccess }) => {
    const { email } = useParams();
    const { trans, i18nConfig } = useI18n();
    const supposedCountry = i18nConfig.bCode.split('_')[1] || 'FR';
    const dispatch = useDispatch();
    const _fetchCountriesIfNeeded = () => dispatch(fetchCountriesIfNeeded());
    const [isRegistering, setIsRegistering] = useState(false);
    const [_clientErrors, setClientErrors] = useState({});
    const [serverErrors, setServerErrors] = useState({});
    const [showErrors, setShowErrors] = useState(false);
    const [isPasswordInputDirty, setIsPasswordInputDirty] = useState(false);
    const [passwordCheckValues, setPasswordCheckValues] = useState({});
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        nationality: supposedCountry,
        email: '',
        confirmEmail: '',
        plainPassword: '',
        birthday: '--',
        phone: '',
        street: '',
        city: '',
        hasAcceptedTOS: false,
        hasAcceptedSCU: false,
    });

    const _validate = dataToValidate => {
        const isHostOrProvider = role === 'host' || role === 'farm';
        const {
            assertNotBlank,
            assertValidEmail,
            assertChecked,
            assertIsEqualTo,
            assertValidPassword,
        } = validators(trans);
        const validatorsList = [
            validate.prop('firstName', assertNotBlank),
            validate.prop('lastName', assertNotBlank),
            validate.prop('email', assertValidEmail),
            validate.prop('hasAcceptedTOS', assertChecked),
            validate.prop('confirmEmail', assertIsEqualTo(R.prop('email', dataToValidate))),
            validate.prop('city', assertNotBlank),
        ];

        validatorsList.push(validate.prop('plainPassword', assertValidPassword));

        if (isHostOrProvider) {
            validatorsList.push(
                validate.prop('street', assertNotBlank),
                validate.prop('phone', assertNotBlank),
                validate.prop('nationality', assertNotBlank),
                validate.prop('hasAcceptedSCU', assertChecked),
                validate.prop(
                    'birthday',
                    validate.unless(dateString => {
                        // valid (beware NaN which is why | 0)
                        const dateObject = R.map(
                            x => parseInt(x, 10) | 0,
                            dateStringToDateObject(dateString)
                        );

                        return (
                            dateObject.day >= 1 &&
                            dateObject.day <= 31 &&
                            dateObject.month >= 1 &&
                            dateObject.month <= 12 &&
                            // the 1900 is completely arbitrary
                            dateObject.year >= 1900 &&
                            dateObject.year <= new Date().getFullYear()
                        );
                    }, trans('formValidation.type.date'))
                )
            );
        }
        const ret = validate.all(validatorsList)(dataToValidate);
        const [, clientErrors] = ret;

        const isPasswordAtLeastOneLowercase = /[a-z]/.test(dataToValidate.plainPassword);
        const isPasswordAtLeastOneUppercase = /[A-Z]/.test(dataToValidate.plainPassword);
        const isPasswordAtLeastEightCharacters = dataToValidate.plainPassword.length > 7;
        const isPasswordAtLeastOneDigit = /[0-9]/.test(dataToValidate.plainPassword);

        setFormData(dataToValidate);
        setClientErrors(clientErrors);
        setPasswordCheckValues({
            isPasswordAtLeastOneLowercase,
            isPasswordAtLeastEightCharacters,
            isPasswordAtLeastOneUppercase,
            isPasswordAtLeastOneDigit,
        });

        return ret;
    };

    const edit = (key, val) => {
        const newFormData = {
            ...formData,
            [key]: val,
        };

        if (key === 'plainPassword') {
            setIsPasswordInputDirty(true);
        }

        _validate(newFormData);
    };

    useEffect(() => {
        _fetchCountriesIfNeeded();

        if (email !== undefined) {
            edit('email', email);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formDataToRequest = () => {
        const isHostOrProvider = role === 'host' || role === 'farm';

        const data = {
            accountSourceApp: 'laruchequiditoui',
            accountSourcePage: 'home',
            address: { city: formData.city },
            email: formData.email,
            firstName: formData.firstName,
            hasAcceptedTOS: formData.hasAcceptedTOS,
            lastName: formData.lastName,
            plainPassword: formData.plainPassword,
            role: role || undefined,
            hive: assemblyId || undefined,
        };

        if (isHostOrProvider) {
            return {
                ...data,
                address: { ...data.address, street: formData.street },
                phone: formData.phone,
                hasAcceptedSCU: formData.hasAcceptedSCU,
                nationality: formData.nationality,
                birthday: formData.birthday,
            };
        }

        return data;
    };

    const submit = e => {
        e.preventDefault();
        e.stopPropagation();

        if (isRegistering) {
            return;
        }

        setShowErrors(true);

        const [isValid] = _validate(formData);

        if (!isValid) {
            return;
        }

        setIsRegistering(true);

        register(formDataToRequest()).then(
            () => {
                setIsRegistering(false);
                onSuccess(formData.email, formData.plainPassword, assemblyId);
            },
            err => {
                setIsRegistering(false);
                setServerErrors((err.body && err.body.detail && err.body.detail.errors) || {});
            }
        );
    };

    return (
        <UserRegistration
            isRegistering={isRegistering}
            clientErrors={_clientErrors}
            serverErrors={serverErrors}
            showErrors={showErrors}
            isPasswordInputDirty={isPasswordInputDirty}
            formData={formData}
            role={role}
            edit={edit}
            submit={submit}
            {...passwordCheckValues}
        />
    );
};

UserRegistrationContainer.propTypes = {
    role: PropTypes.string,
    assemblyId: PropTypes.number,
    onSuccess: PropTypes.func.isRequired,
};

export default UserRegistrationContainer;
